import { useInView } from 'react-intersection-observer'

import classnames from 'classnames'

import Fixture from '~/components/atoms/Fixture/Fixture'
import type { LinkProps } from '~/components/links'
import { resolveLink } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import useFormAnalytics from '~/hooks/useFormAnalytics'

import Button, { ButtonVariants } from '../Button/Button'
import Container from '../Container/Container'
import Text, { TextSizes } from '../Text/Text'

import styles from './BannerCTA.module.css'

export enum BannerVariants {
  primary = 'primary',
  secondary = 'secondary',
}

export interface BannerCTAProps {
  title: string
  subtitle?: string
  variant?: BannerVariants
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  link?: LinkProps & { target?: string }
  launchForm?: string
  testID?: string
  moduleId?: string
}

export default function BannerCTA({
  title,
  subtitle,
  link,
  variant = BannerVariants.primary,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  launchForm,
  testID,
  moduleId,
}: BannerCTAProps) {
  const { handleFormLaunchFromCtaAnalytics } = useFormAnalytics()
  const [ref, inView] = useInView({
    triggerOnce: true,
  })
  const themes = {
    primary: {
      title: TextSizes.magPrimaryTitleMed,
      subtitle: TextSizes.magPrimaryTitleSml,
      button: ButtonVariants.secondaryDark,
      inline: true,
    },
    secondary: {
      title: TextSizes.magPrimaryTitleLrg,
      subtitle: TextSizes.secondaryTitleLrg,
      button: ButtonVariants.primaryDark,
      inline: false,
    },
  }

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      <Container>
        <div
          className={classnames(styles.base, inView && styles.inview)}
          data-testid={testID}
          ref={ref}
        >
          <div className={styles.inner}>
            <div
              className={classnames(
                styles.title,
                !link && !subtitle && styles.noMargin
              )}
            >
              <Text size={themes[variant].title}>{title}</Text>
            </div>
            {subtitle && (
              <div
                className={classnames(styles.title, !link && styles.noMargin)}
              >
                <Text size={themes[variant].subtitle}>{subtitle}</Text>
              </div>
            )}

            <div className={styles.buttonWrap}>
              {link && (
                <Button
                  link={resolveLink(link)}
                  variant={themes[variant].button}
                  iconLeft={true}
                  inline={themes[variant].inline}
                  testID={testID}
                  launchForm={launchForm}
                  gaTrack={link.gaTrack}
                  gaTrackEventName={link.gaTrackEventName}
                  phCTASource={link?.phCTASource}
                  phEventName={link?.phEventName}
                  phInteractionLocation={link?.phInteractionLocation}
                  phWebProduct={link?.phWebProduct}
                >
                  {link?.text}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Container>
    </Fixture>
  )
}
